import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Card,
  Grid,
  Group,
  Menu,
  Modal,
  NavLink,
  Pagination,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconCalendarEvent, IconDots, IconExternalLink, IconFolder, IconPencil, IconPlus, IconRestore, IconSearch, IconTrash } from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  businessToday,
  DATA_BANK_SECTIONS,
  dataBankSection,
  daysBetween,
  formatBytes,
  rcStatus,
  type DataBankEntryDto,
  type DataBankSectionSummaryDto,
  type DataBankTreeNodeDto,
  type Paginated,
} from '@mexp/shared';
import { http } from '@/api/client';
import { useAuth } from '@/auth/AuthProvider';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/States';
import { FileRow } from '@/features/files/FileList';
import { useAction } from '@/lib/useAction';
import { formatDate } from '@/lib/format';
import { DataBankUploadModal } from './DataBankUploadModal';
import { ValidityModal } from './ValidityModal';

const PAGE_SIZE = 50;

/** Expiring within 90 days (yellow) or expired (orange). */
function ValidityBadge({ expiryDate }: { expiryDate: string | null }) {
  const status = rcStatus(expiryDate, businessToday());
  if (status === 'expired') {
    return (
      <Badge size="xs" variant="light" color="orange">
        Expired
      </Badge>
    );
  }
  if (status !== 'expiring') return null;
  const days = daysBetween(businessToday(), expiryDate!);
  return (
    <Badge size="xs" variant="light" color="yellow">
      {days === 0 ? 'Expires today' : `Expires in ${days} d`}
    </Badge>
  );
}

function RenameModal({ section, party, opened, onClose }: { section: string; party: string; opened: boolean; onClose: () => void }) {
  const [name, setName] = useState(party);
  const rename = useAction(() => http.post<{ updated: number }>('/data-bank/rename', { section, party, newParty: name.trim() }), {
    success: 'Party renamed',
    invalidate: [['data-bank']],
    onDone: onClose,
  });
  return (
    <Modal opened={opened} onClose={onClose} title={`Rename “${party}”`}>
      <Stack>
        <Text fz="sm" c="dimmed">
          Renames the party on every document in this section. Stored files are not moved.
        </Text>
        <TextInput label="New name" value={name} onChange={(e) => setName(e.currentTarget.value)} data-autofocus />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!name.trim() || name.trim() === party} loading={rename.isPending} onClick={() => rename.mutate()}>
            Rename
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export function DataBankPage() {
  const { can, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const sectionKey = params.get('section') ?? DATA_BANK_SECTIONS[0]!.key;
  const section = dataBankSection(sectionKey) ?? DATA_BANK_SECTIONS[0]!;
  const [party, setParty] = useState<string | null>(null);
  const [country, setCountry] = useState<string | null>(null);
  const [product, setProduct] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [q] = useDebouncedValue(search, 300);
  const [showDeleted, setShowDeleted] = useState(false);
  const [page, setPage] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [dating, setDating] = useState<DataBankEntryDto | null>(null);
  const isRegistration = section.key === 'registration';
  const canEdit = can('DataBank', 'edit') && user?.userType === 'staff';
  const canDelete = can('DataBank', 'delete');

  const sections = useQuery({ queryKey: ['data-bank', 'sections'], queryFn: () => http.get<{ items: DataBankSectionSummaryDto[] }>('/data-bank/sections') });
  const tree = useQuery({
    queryKey: ['data-bank', 'tree', section.key],
    queryFn: () => http.get<{ items: DataBankTreeNodeDto[] }>('/data-bank/tree', { section: section.key }),
    enabled: section.needsParty,
  });
  const list = useQuery({
    queryKey: ['data-bank', 'entries', section.key, party, country, product, q, showDeleted, page],
    queryFn: () =>
      http.get<Paginated<DataBankEntryDto>>('/data-bank/entries', {
        section: section.key,
        party,
        country,
        product,
        q,
        status: showDeleted ? 'deleted' : 'active',
        page,
        limit: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const partyNode = tree.data?.items.find((p) => p.name === party);
  const countryNode = partyNode?.children?.find((c) => c.name === country);
  const pickSection = (key: string) => {
    setParams({ section: key });
    setParty(null);
    setCountry(null);
    setProduct(null);
    setPage(1);
  };
  const remove = useAction((id: string) => http.post(`/data-bank/entries/${id}/delete`), { success: 'Moved to deleted items', invalidate: [['data-bank']] });
  const restore = useAction((id: string) => http.post(`/data-bank/entries/${id}/restore`), { success: 'Restored', invalidate: [['data-bank']] });
  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / PAGE_SIZE)) : 1;
  const initialContext = useMemo(() => ({ party: party ?? '', country, product: product ?? '' }), [party, country, product]);
  const options = (nodes?: DataBankTreeNodeDto[]) => (nodes ?? []).map((n) => ({ value: n.name, label: `${n.name} (${n.count})` }));

  return (
    <>
      <PageHeader
        title="Data Bank"
        description="Certificates, registrations, dossiers and artworks, filed by exporter, country and product."
        actions={
          canEdit && (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setUploading(true)}>
              Add documents
            </Button>
          )
        }
      />
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 3 }}>
          <Card p="xs">
            {DATA_BANK_SECTIONS.map((s) => (
              <NavLink
                key={s.key}
                label={s.label}
                leftSection={<IconFolder size={16} />}
                rightSection={<Badge size="sm" variant="light">{sections.data?.items.find((x) => x.key === s.key)?.count ?? 0}</Badge>}
                active={s.key === section.key}
                onClick={() => pickSection(s.key)}
                style={{ borderRadius: 'var(--mantine-radius-md)' }}
              />
            ))}
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 9 }}>
          <Card p={0}>
            <Stack gap="xs" p="md">
              <Group justify="space-between">
                <Text fw={600}>{section.label}</Text>
                {canDelete && <Switch label="Show deleted" checked={showDeleted} onChange={(e) => { setShowDeleted(e.currentTarget.checked); setPage(1); }} />}
              </Group>
              <Group gap="sm" wrap="wrap">
                {section.needsParty && (
                  <Group gap={4} wrap="nowrap">
                    <Select
                      placeholder="All parties"
                      data={options(tree.data?.items)}
                      value={party}
                      onChange={(v) => {
                        setParty(v);
                        setCountry(null);
                        setProduct(null);
                        setPage(1);
                      }}
                      searchable
                      clearable
                      w={240}
                    />
                    {party && canEdit && (
                      <Menu withinPortal position="bottom-end">
                        <Menu.Target>
                          <ActionIcon aria-label="Party actions">
                            <IconDots size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item leftSection={<IconPencil size={16} />} onClick={() => setRenaming(true)}>
                            Rename party
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    )}
                  </Group>
                )}
                {section.needsCountry && (
                  <Select
                    placeholder="All countries"
                    data={options(partyNode?.children)}
                    value={country}
                    onChange={(v) => {
                      setCountry(v);
                      setProduct(null);
                      setPage(1);
                    }}
                    disabled={!party}
                    searchable
                    clearable
                    w={200}
                  />
                )}
                {section.needsProduct && (
                  <Select
                    placeholder="All products"
                    data={options(countryNode?.children)}
                    value={product}
                    onChange={(v) => {
                      setProduct(v);
                      setPage(1);
                    }}
                    disabled={!country}
                    searchable
                    clearable
                    w={220}
                  />
                )}
                <TextInput
                  placeholder="Search file names"
                  leftSection={<IconSearch size={16} />}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.currentTarget.value);
                    setPage(1);
                  }}
                  w={220}
                />
              </Group>
            </Stack>
            {list.isLoading ? (
              <LoadingState />
            ) : list.isError ? (
              <ErrorState error={list.error} onRetry={() => void list.refetch()} />
            ) : !list.data?.items.length ? (
              <EmptyState icon={IconFolder} title={showDeleted ? 'No deleted documents' : 'No documents here yet'} />
            ) : (
              <div className="table-scroll">
                <Table miw={760}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Document</Table.Th>
                      {section.needsParty && <Table.Th>Filed under</Table.Th>}
                      {isRegistration && <Table.Th>Valid</Table.Th>}
                      <Table.Th>Added</Table.Th>
                      <Table.Th w={40} />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {list.data.items.map((e) => (
                      <Table.Tr key={e.id}>
                        <Table.Td maw={420}>
                          {e.file ? (
                            <FileRow file={e.file} />
                          ) : e.link ? (
                            <Stack gap={0}>
                              <Anchor href={e.link.url} target="_blank" rel="noopener noreferrer" fz="sm">
                                <Group gap={4} wrap="nowrap">
                                  <IconExternalLink size={14} />
                                  {e.name}
                                </Group>
                              </Anchor>
                              {e.link.notes && (
                                <Text fz="xs" c="dimmed" fs="italic">
                                  {e.link.notes}
                                </Text>
                              )}
                            </Stack>
                          ) : (
                            <Text fz="sm" c="dimmed">
                              {e.name} (file missing)
                            </Text>
                          )}
                        </Table.Td>
                        {section.needsParty && (
                          <Table.Td>
                            <Text fz="sm">{e.party}</Text>
                            <Text fz="xs" c="dimmed">
                              {[e.country, e.product].filter(Boolean).join(' · ')}
                            </Text>
                          </Table.Td>
                        )}
                        {isRegistration && (
                          <Table.Td>
                            <Group gap={6} wrap="nowrap" align="flex-start">
                              <Stack gap={2}>
                                {e.issueDate && e.expiryDate ? (
                                  <>
                                    <Text fz="sm" className="nowrap">
                                      until {formatDate(e.expiryDate)}
                                    </Text>
                                    <Text fz="xs" c="dimmed" className="nowrap">
                                      issued {formatDate(e.issueDate)}
                                    </Text>
                                  </>
                                ) : (
                                  <Text fz="xs" c="dimmed">
                                    Dates not recorded
                                  </Text>
                                )}
                                <ValidityBadge expiryDate={e.expiryDate} />
                              </Stack>
                              {canEdit && e.status === 'active' && (
                                <Tooltip label="Set issue and expiry dates">
                                  <ActionIcon size="sm" onClick={() => setDating(e)} aria-label={`Set the dates of ${e.name}`}>
                                    <IconCalendarEvent size={16} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </Group>
                          </Table.Td>
                        )}
                        <Table.Td>
                          <Text fz="sm" className="nowrap">
                            {formatDate(e.uploadedAt)}
                          </Text>
                          <Text fz="xs" c="dimmed">
                            {e.status === 'deleted' ? `Deleted by ${e.deletedBy ?? '—'}` : (e.uploadedBy ?? '')}
                          </Text>
                          {e.source === 'client' && (
                            <Badge size="xs" variant="light" color="cyan" mt={2}>
                              From client
                            </Badge>
                          )}
                        </Table.Td>
                        <Table.Td>
                          {canDelete &&
                            (e.status === 'deleted' ? (
                              <Tooltip label="Restore">
                                <ActionIcon color="teal" onClick={() => restore.mutate(e.id)} aria-label={`Restore ${e.name}`}>
                                  <IconRestore size={16} />
                                </ActionIcon>
                              </Tooltip>
                            ) : (
                              <Tooltip label="Delete">
                                <ActionIcon color="red" onClick={() => remove.mutate(e.id)} aria-label={`Delete ${e.name}`}>
                                  <IconTrash size={16} />
                                </ActionIcon>
                              </Tooltip>
                            ))}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </div>
            )}
            {list.data && list.data.total > 0 && (
              <Group justify="space-between" p="md">
                <Text fz="sm" c="dimmed">
                  {list.data.total} document{list.data.total === 1 ? '' : 's'}
                  {list.data.items.some((e) => e.file?.sizeBytes) &&
                    ` · ${formatBytes(list.data.items.reduce((s, e) => s + (e.file?.sizeBytes ?? 0), 0))} on this page`}
                </Text>
                {pages > 1 && <Pagination total={pages} value={page} onChange={setPage} />}
              </Group>
            )}
          </Card>
        </Grid.Col>
      </Grid>
      {canEdit && <DataBankUploadModal section={section} opened={uploading} onClose={() => setUploading(false)} initial={initialContext} />}
      {party && <RenameModal section={section.key} party={party} opened={renaming} onClose={() => setRenaming(false)} />}
      <ValidityModal entry={dating} onClose={() => setDating(null)} />
    </>
  );
}
